import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../FirebaseContext';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import './SzablonyDokumentow.css';

const SzablonyDokumentow = () => {
  const { currentFirebase } = useFirebase();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Доступные плейсхолдеры
  const availablePlaceholders = [
    { key: 'DRIVER_NAME', label: 'Imię i nazwisko kierowcy' },
    { key: 'DRIVER_ADDRESS', label: 'Adres kierowcy' },
    { key: 'DRIVER_PESEL', label: 'PESEL kierowcy' },
    { key: 'CAR_MAKE', label: 'Marka samochodu' },
    { key: 'CAR_MODEL', label: 'Model samochodu' },
    { key: 'CAR_VIN', label: 'Numer VIN' },
    { key: 'CAR_REGISTRATION_NUMBER', label: 'Numer rejestracyjny' },
    { key: 'CURRENT_DATE', label: 'Bieżąca data' },
    { key: 'CONTRACT_NUMBER', label: 'Numer umowy' },
    { key: 'COMPANY_NAME', label: 'Nazwa firmy' },
    { key: 'COMPANY_NIP', label: 'NIP firmy' },
    { key: 'COMPANY_ADDRESS', label: 'Adres firmy' },
    { key: 'SIGNATURE_DRIVER', label: 'Miejsce na podpis kierowcy' },
    { key: 'SIGNATURE_COMPANY', label: 'Miejsce na podpis firmy' }
  ];

  // Категории шаблонов
  const templateCategories = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'contract', label: 'Umowy' },
    { value: 'invoice', label: 'Faktury' },
    { value: 'report', label: 'Raporty' },
    { value: 'other', label: 'Inne' }
  ];

  useEffect(() => {
    if (currentFirebase && currentFirebase.firestore) {
      loadTemplates();
    }
  }, [currentFirebase]);

  const loadTemplates = async () => {
    if (!currentFirebase || !currentFirebase.firestore) {
      console.error('Firestore not available');
      return;
    }

    try {
      setLoading(true);
      const templatesCollection = collection(currentFirebase.firestore, 'documentTemplates');
      const querySnapshot = await getDocs(templatesCollection);
      const templatesData = [];
      
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        templatesData.push({ 
          id: docSnapshot.id, 
          ...data,
          createdAt: data.createdAt 
        });
      });
      
      // Сортировка на клиенте
      templatesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      
      setTemplates(templatesData);
    } catch (error) {
      console.error('Błąd podczas ładowania szablonów:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateContent(getDefaultTemplate());
    setSelectedCategory('other');
    setShowEditor(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setTemplateContent(template.content);
    setSelectedCategory(template.category || 'other');
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !templateContent.trim()) {
      alert('Proszę wypełnić nazwę i treść szablonu');
      return;
    }

    if (!currentFirebase || !currentFirebase.firestore) {
      alert('Brak połączenia z bazą danych');
      return;
    }

    try {
      setLoading(true);
      const currentUser = currentFirebase.auth?.currentUser;
      const templateData = {
        name: templateName,
        description: templateDescription,
        content: templateContent,
        category: selectedCategory !== 'all' ? selectedCategory : 'other',
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || 'admin'
      };

      if (editingTemplate) {
        await updateDoc(doc(currentFirebase.firestore, 'documentTemplates', editingTemplate.id), templateData);
      } else {
        await addDoc(collection(currentFirebase.firestore, 'documentTemplates'), {
          ...templateData,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.email || 'admin'
        });
      }

      await loadTemplates();
      setShowEditor(false);
      resetForm();
    } catch (error) {
      console.error('Błąd podczas zapisywania szablonu:', error);
      alert('Wystąpił błąd podczas zapisywania szablonu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!currentFirebase || !currentFirebase.firestore) {
      alert('Brak połączenia z bazą danych');
      return;
    }

    if (window.confirm('Czy na pewno chcesz usunąć ten szablon?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(currentFirebase.firestore, 'documentTemplates', templateId));
        await loadTemplates();
      } catch (error) {
        console.error('Błąd podczas usuwania szablonu:', error);
        alert('Wystąpił błąd podczas usuwania szablonu');
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePreview = () => {
    let content = templateContent;
    
    // Przykładowe dane do podglądu
    const sampleData = {
      DRIVER_NAME: 'Jan Kowalski',
      DRIVER_ADDRESS: 'ul. Przykładowa 123, 00-000 Warszawa',
      DRIVER_PESEL: '12345678901',
      CAR_MAKE: 'Mercedes',
      CAR_MODEL: 'Sprinter',
      CAR_VIN: 'WDB9061331N123456',
      CAR_REGISTRATION_NUMBER: 'WA 12345',
      CURRENT_DATE: new Date().toLocaleDateString('pl-PL'),
      CONTRACT_NUMBER: 'UM/2024/001',
      COMPANY_NAME: 'Transport Sp. z o.o.',
      COMPANY_NIP: '1234567890',
      COMPANY_ADDRESS: 'ul. Firmowa 456, 00-000 Warszawa',
      SIGNATURE_DRIVER: '<div class="signature-field"><p>Podpis kierowcy</p><div class="signature-line"></div></div>',
      SIGNATURE_COMPANY: '<div class="signature-field"><p>Podpis przedstawiciela firmy</p><div class="signature-line"></div></div>'
    };

    // Zamiana placeholderów
    Object.keys(sampleData).forEach(key => {
      const placeholder = `PLACEHOLDER_${key}`;
      content = content.replace(new RegExp(placeholder, 'g'), sampleData[key]);
    });

    setPreviewContent(content);
    setShowPreview(true);
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('templateEditor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateContent;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    setTemplateContent(before + `PLACEHOLDER_${placeholder}` + after);
    
    // Ustaw kursor za wstawionym placeholderem
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length + 12;
      textarea.focus();
    }, 0);
  };

  const resetForm = () => {
    setTemplateName('');
    setTemplateDescription('');
    setTemplateContent('');
    setEditingTemplate(null);
    setSelectedCategory('all');
  };

  const getDefaultTemplate = () => {
    return `<!DOCTYPE html>
<html lang="pl">
<head>
    <meta charset="UTF-8">
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 40px;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .content {
            margin-bottom: 30px;
        }
        .signature-field {
            margin-top: 50px;
            width: 300px;
            display: inline-block;
            text-align: center;
        }
        .signature-line {
            border-bottom: 1px solid #000;
            margin-top: 60px;
        }
        .signatures-container {
            display: flex;
            justify-content: space-between;
            margin-top: 50px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
        }
        th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
        }
        th {
            background-color: #f4f4f4;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>NAZWA DOKUMENTU</h1>
        <p>Nr: PLACEHOLDER_CONTRACT_NUMBER</p>
        <p>Data: PLACEHOLDER_CURRENT_DATE</p>
    </div>
    
    <div class="content">
        <p>Tutaj wpisz treść dokumentu...</p>
        
        <h3>Dane kierowcy:</h3>
        <p>Imię i nazwisko: PLACEHOLDER_DRIVER_NAME</p>
        <p>Adres: PLACEHOLDER_DRIVER_ADDRESS</p>
        <p>PESEL: PLACEHOLDER_DRIVER_PESEL</p>
        
        <h3>Dane pojazdu:</h3>
        <p>Marka: PLACEHOLDER_CAR_MAKE</p>
        <p>Model: PLACEHOLDER_CAR_MODEL</p>
        <p>Numer VIN: PLACEHOLDER_CAR_VIN</p>
        <p>Numer rejestracyjny: PLACEHOLDER_CAR_REGISTRATION_NUMBER</p>
    </div>
    
    <div class="signatures-container">
        PLACEHOLDER_SIGNATURE_DRIVER
        PLACEHOLDER_SIGNATURE_COMPANY
    </div>
</body>
</html>`;
  };

  const filteredTemplates = templates.filter(template => 
    selectedCategory === 'all' || template.category === selectedCategory
  );

  // Если Firestore не загружен, показываем загрузку
  if (!currentFirebase || !currentFirebase.firestore) {
    return (
      <div className="szablony-container">
        <div className="loading">
          <p>Ładowanie połączenia z bazą danych...</p>
          <p style={{ fontSize: '14px', marginTop: '10px', color: '#666' }}>
            Sprawdź czy wybrałeś projekt w górnym menu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="szablony-container">
      <div className="szablony-header">
        <h1>Szablony dokumentów</h1>
        <button 
          className="btn-primary"
          onClick={handleCreateTemplate}
          disabled={loading}
        >
          + Nowy szablon
        </button>
      </div>

      <div className="filter-section">
        <select 
          value={selectedCategory} 
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-filter"
        >
          {templateCategories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {loading && <div className="loading">Ładowanie...</div>}

      {templates.length === 0 && !loading && (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <h3>Brak szablonów</h3>
          <p>Kliknij "+ Nowy szablon" aby utworzyć pierwszy szablon dokumentu.</p>
        </div>
      )}

      <div className="templates-grid">
        {filteredTemplates.map(template => (
          <div key={template.id} className="template-card">
            <h3>{template.name}</h3>
            <p className="template-description">{template.description || 'Brak opisu'}</p>
            <div className="template-meta">
              <span>Kategoria: {template.category || 'Inne'}</span>
              <span>Utworzony: {template.createdAt?.toDate?.()?.toLocaleDateString('pl-PL') || 'Nieznana data'}</span>
            </div>
            <div className="template-actions">
              <button onClick={() => handleEditTemplate(template)}>Edytuj</button>
              <button onClick={() => handleDeleteTemplate(template.id)} className="btn-danger">Usuń</button>
            </div>
          </div>
        ))}
      </div>

      {showEditor && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h2>{editingTemplate ? 'Edytuj szablon' : 'Nowy szablon'}</h2>
              <button className="close-button" onClick={() => {
                setShowEditor(false);
                resetForm();
              }}>×</button>
            </div>

            <div className="editor-container">
              <div className="editor-main">
                <input
                  type="text"
                  placeholder="Nazwa szablonu"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="template-name-input"
                />
                
                <input
                  type="text"
                  placeholder="Opis szablonu (opcjonalnie)"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="template-description-input"
                />

                <select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                >
                  {templateCategories.filter(cat => cat.value !== 'all').map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>

                <textarea
                  id="templateEditor"
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  className="template-editor"
                  placeholder="Treść szablonu HTML..."
                />
              </div>

              <div className="editor-sidebar">
                <h3>Dostępne zmienne</h3>
                <div className="placeholders-list">
                  {availablePlaceholders.map(placeholder => (
                    <div 
                      key={placeholder.key}
                      className="placeholder-item"
                      onClick={() => insertPlaceholder(placeholder.key)}
                    >
                      <code>{placeholder.key}</code>
                      <small>{placeholder.label}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handlePreview} className="btn-secondary">Podgląd</button>
              <button onClick={handleSaveTemplate} className="btn-primary" disabled={loading}>
                {loading ? 'Zapisywanie...' : 'Zapisz'}
              </button>
              <button onClick={() => {
                setShowEditor(false);
                resetForm();
              }} className="btn-secondary">Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="modal-overlay">
          <div className="modal-content preview-modal">
            <div className="modal-header">
              <h2>Podgląd dokumentu</h2>
              <button className="close-button" onClick={() => setShowPreview(false)}>×</button>
            </div>
            <div 
              className="preview-content"
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SzablonyDokumentow;